import React, { useEffect, useState } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import Modal from 'components/Modal/Modal';
import RoundedButton from 'components/RoundedButton/RoundedButton';

import styles from '../DbSnapshots.module.scss';

const isSafeUrl = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const PLATFORMS = [
  { id: 'macos', label: 'macOS', install: 'brew install gnupg' },
  { id: 'linux', label: 'Linux', install: 'sudo apt-get install -y gnupg  # or your distro equivalent' },
];

const filenameFromUrl = (url) => {
  try {
    const path = new URL(url).pathname;
    const last = path.split('/').pop() || 'snapshot.sql.gz.gpg';
    return decodeURIComponent(last);
  } catch {
    return 'snapshot.sql.gz.gpg';
  }
};

const buildDecryptCommand = (password, encryptedFile) => {
  const outputFile = encryptedFile.replace(/\.sql\.gz\.gpg$/, '.sql');
  const escapedPass = password.replace(/'/g, "'\\''");
  // Leading space relies on HISTCONTROL=ignorespace (bash) / HIST_IGNORE_SPACE (zsh)
  // to keep the passphrase out of shell history.
  return [
    ` echo '${escapedPass}' | gpg --batch --pinentry-mode loopback`,
    `--passphrase-fd 0 --decrypt '${encryptedFile}' | gunzip > '${outputFile}'`,
  ].join(' ');
};

const DownloadPasswordModal = ({ isOpen, onClose, password, downloadUrl, onConfirm }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [platform, setPlatform] = useState('macos');
  const [cmdCopied, setCmdCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setConfirmed(false);
      setRevealed(false);
      setCopied(false);
      setPlatform('macos');
      setCmdCopied(false);
    }
  }, [isOpen]);

  const handleCopy = async () => {
    if (!password) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(password);
      }
      setCopied(true);
    } catch (error) {
      setCopied(false);
    }
  };

  const handleConfirm = () => {
    setConfirmed(true);
    onConfirm?.();
  };

  const encryptedFile = downloadUrl ? filenameFromUrl(downloadUrl) : 'snapshot.sql.gz.gpg';
  const decryptCommand = password ? buildDecryptCommand(password, encryptedFile) : '';
  const activePlatform = PLATFORMS.find((p) => p.id === platform) ?? PLATFORMS[0];

  const handleCopyCommand = async () => {
    if (!decryptCommand) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(decryptCommand);
      }
      setCmdCopied(true);
    } catch (error) {
      setCmdCopied(false);
    }
  };

  return (
    <Modal
      title='Download snapshot'
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      styles={{ minWidth: 480 }}
    >
      <div className={cx(styles.modalBody, 'p-3 d-flex flex-column gap-3')}>
        {!confirmed ? (
          <>
            <p className={styles.warning} role='alert'>
              This password is shown <strong>only once</strong>. Save it before continuing — you will not be able to
              retrieve it again.
            </p>

            <div className={styles.passwordBox}>
              <span className='form-label'>Archive password</span>
              <div className='d-flex align-items-center gap-2'>
                <code data-testid='password-value' className={cx(styles.passwordValue)}>
                  {revealed ? password : '•'.repeat(Math.min(password?.length || 16, 24))}
                </code>
                <RoundedButton
                  text={revealed ? 'Hide' : 'Reveal'}
                  color='gray'
                  size='sm'
                  btnStyle='outlined'
                  onClick={() => setRevealed((prev) => !prev)}
                />
                <RoundedButton
                  text={copied ? 'Copied' : 'Copy'}
                  color='primary'
                  size='sm'
                  btnStyle='outlined'
                  onClick={handleCopy}
                  isDisabled={!password}
                />
              </div>
            </div>

            <div className='d-flex justify-content-end gap-2'>
              <RoundedButton text='Cancel' color='gray' btnStyle='outlined' onClick={onClose} />
              <RoundedButton
                text='I copied it — continue'
                color='primary'
                onClick={handleConfirm}
                isDisabled={!password}
              />
            </div>
          </>
        ) : (
          <>
            <p>The download will start now. The password is no longer accessible.</p>
            {downloadUrl && isSafeUrl(downloadUrl) && (
              <a
                href={downloadUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='btn btn-primary'
                data-testid='download-link'
              >
                Start download
              </a>
            )}

            <div className='mt-2'>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <span className='form-label mb-0'>Decrypt command</span>
                <div className='d-flex gap-1'>
                  {PLATFORMS.map((p) => (
                    <button
                      key={p.id}
                      type='button'
                      className={cx('btn btn-sm', platform === p.id ? 'btn-primary' : 'btn-outline-secondary')}
                      onClick={() => setPlatform(p.id)}
                      data-testid={`platform-${p.id}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className='text-muted mb-2' style={{ fontSize: 12 }}>
                Install once: <code>{activePlatform.install}</code>
              </p>
              <div className='d-flex align-items-stretch gap-2'>
                <pre
                  className={cx(styles.passwordBox, 'flex-grow-1 mb-0')}
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 12, padding: 8 }}
                  data-testid='decrypt-command'
                >
                  {decryptCommand}
                </pre>
                <RoundedButton
                  text={cmdCopied ? 'Copied' : 'Copy'}
                  color='primary'
                  size='sm'
                  btnStyle='outlined'
                  onClick={handleCopyCommand}
                  isDisabled={!decryptCommand}
                />
              </div>
              <p className='text-muted mt-2 mb-0' style={{ fontSize: 12 }}>
                Run from the directory containing the downloaded file. Leading space keeps the passphrase out of shell
                history when <code>HISTCONTROL=ignorespace</code> (bash) or <code>setopt HIST_IGNORE_SPACE</code> (zsh)
                is set.
              </p>
            </div>

            <div className='d-flex justify-content-end'>
              <RoundedButton text='Close' color='gray' onClick={onClose} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

DownloadPasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  password: PropTypes.string,
  downloadUrl: PropTypes.string,
  onConfirm: PropTypes.func,
};

DownloadPasswordModal.defaultProps = {
  password: '',
  downloadUrl: '',
  onConfirm: undefined,
};

export default DownloadPasswordModal;
